import type { FC } from "react";
import { twMerge } from "tailwind-merge";
import {
  formatFunnelPreciseShare,
  formatFunnelShare,
  funnelColorClassNames,
  type FunnelMomentPart,
} from "./funnelFormatting.js";

export interface FunnelMomentShareBarProps {
  momentName: string;
  /** Of the stage's interactions, those with the moment. */
  share: number;
  /** The moment's interactions, split as the bar draws them. */
  parts: readonly FunnelMomentPart[];
  /** The stage's interactions in prose, from `formatFunnelStageInteractions`. */
  stageInteractions: string;
  /** The slideover's full-width bar, without the trailing percentage. */
  isExpanded?: boolean;
}

/** The moment's share of the stage, split into its parts in the flow's order. */
export const FunnelMomentShareBar: FC<FunnelMomentShareBarProps> = (props) => {
  const {
    momentName,
    share,
    parts,
    stageInteractions,
    isExpanded = false,
  } = props;
  const interactions = parts.reduce((sum, part) => sum + part.interactions, 0);
  // Each part's share of the moment's interactions.
  const segments = parts.map((part) => ({
    ...part,
    rate: interactions > 0 ? part.interactions / interactions : 0,
  }));
  const partsDescription =
    segments.length > 1
      ? `: ${segments
          .map(
            (segment) =>
              `${formatFunnelPreciseShare(share * segment.rate)} ${segment.color === "continued" ? segment.label.toLowerCase() : segment.label}`,
          )
          .join(", ")}`
      : "";
  const description = `${formatFunnelShare(share)} of ${stageInteractions} had this moment${partsDescription}.`;
  // A soft corner: 4px would round the compact bar's 8px height into a pill.
  const segmentClassName = twMerge(
    "h-full",
    isExpanded ? "rounded-sm" : "rounded-xs",
  );

  return (
    <span
      className={twMerge(
        "flex shrink-0 items-center gap-3",
        isExpanded ? "w-full" : "w-44 sm:w-64",
      )}
      title={description}
    >
      <span
        role="img"
        aria-label={`${momentName}. ${description}`}
        className={twMerge(
          "flex flex-1",
          isExpanded ? "h-3 gap-0.5" : "h-2 gap-px",
        )}
      >
        {/* A part without interactions, like no one continuing, isn't drawn. */}
        {segments
          .filter((segment) => segment.interactions > 0)
          .map((segment) => (
            <span
              key={segment.key}
              // At least 2px, so a sliver of a part still shows past the gaps.
              className={twMerge(
                segmentClassName,
                "min-w-0.5",
                funnelColorClassNames[segment.color].background,
              )}
              style={{ width: `${share * segment.rate * 100}%` }}
            />
          ))}
        {/* The stage's interactions without the moment. */}
        {share < 1 && (
          <span
            className={twMerge(segmentClassName, "bg-border-strong/40")}
            style={{ width: `${(1 - share) * 100}%` }}
          />
        )}
      </span>
      {!isExpanded && (
        <span className="w-9 text-right text-xs text-muted-foreground tabular-nums">
          {formatFunnelShare(share)}
        </span>
      )}
    </span>
  );
};
